<template>
  <div class="flex flex-col p-4 sm:p-6 h-full overflow-y-auto">
    <!-- Delete Modal -->
    <transition name="modal">
      <delete
        v-if="showDeleteModal"
        :loading="loadingDelete"
        @close="close"
        @confirmDelete="deleteElement"
      ></delete>
    </transition>

    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 flex-shrink-0">
      <div>
        <h1 class="text-xl sm:text-2xl font-bold text-white">Gestión de Oportunidades</h1>
        <p class="mt-1 text-xs sm:text-sm text-content-300">
          Administra las oportunidades de inversión
        </p>
      </div>
      <router-link
        class="primary-button mt-4 sm:mt-0"
        :to="{ name: 'adminOpportunitiesForm' }"
      >
        Agregar Oportunidad
      </router-link>
    </div>

    <!-- Filters -->
    <div class="bg-gradient-to-r from-content-800 to-content-700 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 flex-shrink-0">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
        <div>
          <label class="block text-xs sm:text-sm font-medium text-white mb-2">Estado</label>
          <select
            v-model="selectedStatusFilter"
            class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent text-sm"
          >
            <option value="active">Activas y en progreso</option>
            <option value="all">Todas</option>
            <option value="draft">Solo borradores</option>
            <option value="active-only">Solo activas</option>
            <option value="paused">Solo pausadas</option>
            <option value="closed">Solo cerradas</option>
          </select>
        </div>

        <div class="flex items-end">
          <div class="text-xs sm:text-sm text-content-200">
            Mostrando {{ filteredOpportunities.length }} de {{ opportunities.length }} oportunidades
          </div>
        </div>

        <div class="flex items-end justify-end">
          <div
            v-if="selectedStatusFilter !== 'active'"
            class="px-3 py-1 bg-primary-600 text-white text-xs sm:text-sm rounded-lg"
          >
            Filtro: {{ getFilterLabel(selectedStatusFilter) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6 flex-shrink-0">
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-3 sm:p-4 rounded-lg">
        <div class="text-xl sm:text-2xl font-bold text-blue-400">{{ stats.draft }}</div>
        <div class="text-xs sm:text-sm text-content-300">Borradores</div>
      </div>
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-3 sm:p-4 rounded-lg">
        <div class="text-xl sm:text-2xl font-bold text-green-400">{{ stats.active }}</div>
        <div class="text-xs sm:text-sm text-content-300">Activas</div>
      </div>
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-3 sm:p-4 rounded-lg">
        <div class="text-xl sm:text-2xl font-bold text-yellow-400">{{ stats.paused }}</div>
        <div class="text-xs sm:text-sm text-content-300">Pausadas</div>
      </div>
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-3 sm:p-4 rounded-lg">
        <div class="text-xl sm:text-2xl font-bold text-red-400">{{ stats.closed }}</div>
        <div class="text-xs sm:text-sm text-content-300">Cerradas</div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12 flex-shrink-0">
      <loader-icon class="h-10 w-10 text-primary-500"></loader-icon>
    </div>

    <!-- Empty States -->
    <div v-else-if="filteredOpportunities.length === 0 && opportunities.length > 0" class="bg-gradient-to-r from-content-800 to-content-700 rounded-lg p-8 sm:p-12 text-center flex-shrink-0">
      <div class="text-content-400">
        <svg class="mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <div class="text-base sm:text-lg text-white mb-2">No hay oportunidades que coincidan con el filtro</div>
        <div class="text-xs sm:text-sm text-content-300">Cambia el filtro para ver más oportunidades</div>
      </div>
    </div>

    <div v-else-if="opportunities.length === 0" class="bg-gradient-to-r from-content-800 to-content-700 rounded-lg p-8 sm:p-12 text-center flex-shrink-0">
      <div class="text-content-400">
        <svg class="mx-auto h-10 w-10 sm:h-12 sm:w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        <div class="text-base sm:text-lg text-white mb-2">No hay oportunidades creadas</div>
        <div class="text-xs sm:text-sm text-content-300">Usa el botón "Agregar Oportunidad" para crear tu primera oportunidad de inversión</div>
      </div>
    </div>

    <!-- Opportunities Table -->
    <div v-else class="overflow-x-auto mt-4 sm:mt-6 flex-shrink-0 pb-4">
      <table class="w-full table-fixed text-sm rounded-lg shadow-primary">
          <colgroup>
            <col style="width: 28%;">
            <col class="hidden sm:table-column" style="width: 18%;">
            <col class="hidden md:table-column" style="width: 18%;">
            <col style="width: 14%;">
            <col class="hidden md:table-column" style="width: 12%;">
            <col class="hidden sm:table-column" style="width: 10%;">
          </colgroup>
          <thead class="bg-content-700">
            <tr>
              <th class="px-2 sm:px-3 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-tight">
                Oportunidad
              </th>
              <th class="hidden sm:table-cell px-2 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-tight">
                Instr. / Tasa
              </th>
              <th class="hidden md:table-cell px-2 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-tight">
                Progreso
              </th>
              <th class="px-2 sm:px-3 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-tight">
                Estado
              </th>
              <th class="hidden md:table-cell px-2 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-tight">
                Fecha
              </th>
              <th class="hidden sm:table-cell px-2 py-3 text-center text-xs font-medium text-content-200 uppercase tracking-tight">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody class="text-white">
            <tr v-for="opportunity in filteredOpportunities" :key="opportunity.id" 
                class="cursor-pointer hover:bg-content-600 sm:cursor-default"
                @click="$router.push({ name: 'adminOpportunityDetail', params: { id: opportunity.id } })"
            >
              <!-- Opportunity Info -->
              <td class="px-2 sm:px-3 py-3">
                <div>
                  <div class="text-xs font-medium text-white truncate" :title="opportunity.title">
                    {{ opportunity.title }}
                  </div>
                  <div class="text-xs text-content-300 mt-1 line-clamp-1" :title="opportunity.description || opportunity.content">
                    {{ opportunity.description || opportunity.content }}
                  </div>
                  <!-- Investment type tags -->
                  <div class="flex flex-wrap gap-1 mt-1">
                    <span v-if="opportunity.enableDebt" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-600 text-white">
                      Deuda
                    </span>
                    <span v-if="opportunity.enableCoOwnership" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-600 text-white">
                      Adquisición de inmueble
                    </span>
                  </div>
                  <!-- Mobile-only info -->
                  <div class="sm:hidden mt-1 text-xs">
                    <span v-if="opportunity.enableDebt" class="text-green-400 mr-2">
                      {{ opportunity.annualFixedRate }}%
                    </span>
                    <span v-if="opportunity.enableCoOwnership" class="text-blue-400">
                      {{ opportunity.estimatedIRR }}% TIR
                    </span>
                  </div>
                </div>
              </td>

              <!-- Instrument / Rate -->
              <td class="hidden sm:table-cell px-2 py-3">
                <div class="space-y-1">
                  <div v-if="opportunity.enableDebt" class="text-xs">
                    <span class="text-green-400 font-semibold">{{ opportunity.annualFixedRate }}%</span>
                  </div>
                  <div v-if="opportunity.enableCoOwnership" class="text-xs">
                    <span class="text-blue-400 font-semibold">{{ opportunity.estimatedIRR }}%</span>
                    <span class="text-content-400 text-xs ml-1">TIR</span>
                  </div>
                  <div v-if="!opportunity.enableDebt && !opportunity.enableCoOwnership" class="text-xs text-content-400">
                    N/A
                  </div>
                </div>
              </td>

              <!-- Progress -->
              <td class="hidden md:table-cell px-2 py-3">
                <div v-if="opportunity.maxAmount">
                  <div class="text-xs text-white mb-1 truncate">
                    {{ formatCurrencyShort(getTotalRaised(opportunity)) }} / {{ formatCurrencyShort(opportunity.maxAmount) }}
                  </div>
                  <div class="w-full bg-content-600 rounded-full h-1.5 mb-1">
                    <div
                      class="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
                      :style="{ width: getProgressPercentage(opportunity) + '%' }"
                    ></div>
                  </div>
                  <div class="text-xs text-content-300">
                    {{ getProgressPercentage(opportunity) }}%
                  </div>
                </div>
                <div v-else class="text-xs text-content-400">
                  N/A
                </div>
              </td>

              <!-- Status -->
              <td class="px-2 sm:px-3 py-3">
                <span
                  class="inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap"
                  :class="getStatusClass(opportunity.status)"
                >
                  {{ getStatusLabel(opportunity.status) }}
                </span>
              </td>

              <!-- Date -->
              <td class="hidden md:table-cell px-2 py-3">
                <div class="text-xs text-content-300 whitespace-nowrap">
                  {{ formatDate(opportunity.createdAt) }}
                </div>
              </td>

              <!-- Actions -->
              <td class="hidden sm:table-cell px-2 py-3 text-center"
                  @click.stop>
                <div class="flex flex-col gap-1">
                  <router-link
                    :to="{
                      name: 'adminOpportunityDetail',
                      params: { id: opportunity.id },
                    }"
                    class="text-xs px-2 py-1 bg-content-600 hover:bg-content-500 text-white rounded smooth"
                    title="Ver detalles"
                  >
                    Ver
                  </router-link>

                  <router-link
                    :to="{
                      name: 'adminOpportunitiesEditForm',
                      params: { id: opportunity.id },
                    }"
                    class="text-xs px-2 py-1 bg-content-600 hover:bg-content-500 text-white rounded smooth"
                    title="Editar"
                  >
                    Editar
                  </router-link>

                  <button
                    @click="
                      opportunityToDelete = opportunity;
                      openDeleteModal();
                    "
                    class="text-xs px-2 py-1 bg-content-600 hover:bg-content-500 text-white rounded smooth"
                    title="Eliminar"
                  >
                    Elim
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
    </div>
  </div>
</template>

<script>
import api from "../../../../api";
import deleteMixin from "../../../../mixins/deleteMixin";

export default {
  name: "OpportunitiesIndex",
  mixins: [deleteMixin],
  
  data() {
    return {
      opportunities: [],
      opportunityToUpdate: null,
      selectedStatusFilter: 'active',
      loading: false,
      stats: {
        draft: 0,
        active: 0,
        paused: 0,
        closed: 0
      }
    };
  },

  computed: {
    filteredOpportunities() {
      if (!this.opportunities) return [];
      
      switch (this.selectedStatusFilter) {
        case 'active':
          return this.opportunities.filter(opp => opp.status !== 'closed');
        case 'all':
          return this.opportunities;
        case 'draft':
          return this.opportunities.filter(opp => opp.status === 'draft');
        case 'active-only':
          return this.opportunities.filter(opp => opp.status === 'active');
        case 'paused':
          return this.opportunities.filter(opp => opp.status === 'paused');
        case 'closed':
          return this.opportunities.filter(opp => opp.status === 'closed');
        default:
          return this.opportunities.filter(opp => opp.status !== 'closed');
      }
    }
  },

  created() {
    this.$store.commit("setSelectedTab", "adminOpportunities");
    this.loadOpportunities();
  },

  methods: {
    async loadOpportunities() {
      this.loading = true;
      
      try {
        const response = await api.opportunities.getAll();
        this.opportunities = response.data || [];
        this.calculateStats();
      } catch (error) {
        console.error('Error loading opportunities:', error);
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al cargar oportunidades");
      }
      
      this.loading = false;
    },

    calculateStats() {
      this.stats = {
        draft: this.opportunities.filter(o => o.status === 'draft').length,
        active: this.opportunities.filter(o => o.status === 'active').length,
        paused: this.opportunities.filter(o => o.status === 'paused').length,
        closed: this.opportunities.filter(o => o.status === 'closed').length
      };
    },

    getTotalRaised(opportunity) {
      const intentionAmount = parseFloat(opportunity.intentionAmount) || 0;
      const closedAmount = parseFloat(opportunity.closedAmount) || 0;
      return intentionAmount + closedAmount;
    },

    getProgressPercentage(opportunity) {
      const maxAmount = parseFloat(opportunity.maxAmount) || 0;
      if (maxAmount === 0) return 0;
      const totalRaised = this.getTotalRaised(opportunity);
      return Math.min(Math.round((totalRaised / maxAmount) * 100), 100);
    },

    getFilterLabel(filter) {
      const labels = {
        all: 'Todas',
        draft: 'Borradores',
        'active-only': 'Activas',
        paused: 'Pausadas',
        closed: 'Cerradas'
      };
      return labels[filter] || filter;
    },

    getStatusLabel(status) {
      const labels = {
        draft: 'Borrador',
        active: 'Activa',
        paused: 'Pausada',
        closed: 'Cerrada'
      };
      return labels[status] || status;
    },

    getStatusClass(status) {
      const classes = {
        draft: 'bg-gray-800 text-gray-300',
        active: 'bg-green-800 text-green-300',
        paused: 'bg-yellow-800 text-yellow-300',
        closed: 'bg-red-800 text-red-300'
      };
      return classes[status] || 'bg-gray-800 text-gray-300';
    },

    formatDate(datetime) {
      if (!datetime) return "-";
      
      return new Date(datetime).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatCurrency(amount) {
      const numericAmount = parseFloat(amount) || 0;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numericAmount);
    },

    formatCurrencyShort(amount) {
      const numericAmount = parseFloat(amount) || 0;
      if (numericAmount >= 1000000) {
        return '$' + (numericAmount / 1000000).toFixed(1) + 'M';
      } else if (numericAmount >= 1000) {
        return '$' + (numericAmount / 1000).toFixed(0) + 'K';
      }
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numericAmount);
    },

    close() {
      this.opportunityToDelete = null;
      this.closeDeleteModal();
    },

    async deleteElement() {
      this.closeDeleteModal();
      this.loadingDelete = true;

      try {
        const response = await api.opportunities.delete(this.opportunityToDelete.id);
        this.$store.dispatch("successSnack", response.message);
        this.opportunityToDelete = null;
        this.loadOpportunities();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al eliminar oportunidad");
      }

      this.loadingDelete = false;
    },

    getInstrumentLabel(instrument) {
      const labels = {
        'Debt': 'Deuda',
        'Co-ownership': 'Adquisición de inmueble'
      };
      return labels[instrument] || instrument;
    }
  }
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>